import { Fragment, useState } from 'react';

import SemenSearch from '../../semen/forms/search';
import { IconPlus, IconTrash } from '../../../common/icons';
import { STAGES, PRESERVATION, embryosCreated, stagesSum } from '../../../core/constants/clivage';

// Edición de clivage organizada POR DONADORA (flujo v1): cada donadora es una
// fila-encabezado (sin columna "Donante" → no desborda) con sus clivages apilados
// debajo; el "+" compacto va en línea con el ÚLTIMO clivage (append por donadora);
// encabezado con botón de desplegar/colapsar. Restricciones de edición: semen
// congelado y preservación de solo lectura si el clivage ya tiene embriones; el
// piso (mín embryos_count) se muestra por clivage.

function Chevron({ open }) {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
        </svg>
    );
}

export default function ClivageEditRows({ rows, errors, donors, sameSemen, onField, onStage, onSemen, onClearSemen, onSexed, onRemove, onAddClivage }) {
    const [collapsed, setCollapsed] = useState(() => new Set());
    const toggle = (k) => setCollapsed((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

    const donorByAsp = Object.fromEntries((donors || []).map((d) => [String(d.aspiration_id), d]));
    // grupos en el orden de `rows` (respeta reorganizar) + donadoras sin clivage al final.
    const groups = [];
    const gidx = new Map();
    rows.forEach((r) => {
        const k = String(r.aspiration_id);
        if (!gidx.has(k)) { gidx.set(k, groups.length); groups.push({ key: k, donor: donorByAsp[k] || { aspiration_id: r.aspiration_id, name: r.donorName, number: r.donorNumber, viables: r.viables }, items: [] }); }
        groups[gidx.get(k)].items.push(r);
    });
    (donors || []).forEach((d) => { const k = String(d.aspiration_id); if (!gidx.has(k)) groups.push({ key: k, donor: d, items: [] }); });
    const colSpan = (sameSemen ? 0 : 1) + STAGES.length + 5;

    const fields = (r) => {
        const e = errors[r.key] || {};
        const isNew = !r.clivageId;
        const frozenSemen = !isNew && r.embryosFloor > 0;
        const embryos = embryosCreated(r.stages);
        const structures = stagesSum(r.stages);
        return (
            <>
                {!sameSemen && (
                    <td className="cl-td-semen">
                        {frozenSemen ? (
                            <span className="cl-pres-ro" title="Semen congelado: el clivage ya tiene embriones">{r.semen?.code || '—'}</span>
                        ) : (
                            <SemenSearch value={r.semen} onSelect={(s) => onSemen(r.key, s)} onClear={() => onClearSemen(r.key)} />
                        )}
                        {e.semen && <span className="cl-cell-error" role="alert">{e.semen}</span>}
                    </td>
                )}
                <td className="cl-td-num">
                    <input type="number" min="0" inputMode="numeric" title={e.oocytes || undefined}
                        className={`cl-num-input ${e.oocytes ? 'cl-input--bad' : ''}`}
                        value={r.oocytes} onChange={(ev) => onField(r.key, 'oocytes', ev.target.value)} />
                </td>
                {STAGES.map((s) => (
                    <td key={s.value} className="cl-td-num">
                        <input type="number" min="0" inputMode="numeric" className="cl-num-input cl-stage-input" style={{ '--stage-color': s.color }}
                            value={r.stages?.[s.value] ?? ''} onChange={(ev) => onStage(r.key, s.value, ev.target.value)} placeholder="0" />
                    </td>
                ))}
                <td className="cl-td-num cl-td-emb">
                    <input type="number" value={embryos} readOnly tabIndex={-1} title={e.embryos || (structures !== embryos ? `${structures} estructuras (incl. DG)` : undefined)}
                        className={`cl-num-input cl-input--readonly ${e.embryos ? 'cl-input--bad' : ''}`} />
                    {!isNew && r.embryosFloor > 0 && <span className="cl-emb-floor" title="No baja del embryos_count actual (incl. DG)">mín {r.embryosFloor}</span>}
                </td>
                <td className="cl-td-pres">
                    {isNew ? (
                        <select value={r.preservation || 'fresh'} onChange={(ev) => onField(r.key, 'preservation', ev.target.value)}>
                            {PRESERVATION.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                    ) : (
                        <span className="cl-pres-ro" title="La preservación se gestiona en el módulo de embriones">—</span>
                    )}
                </td>
                <td className="cl-td-sex">
                    <input type="checkbox" checked={!!r.sexed} onChange={(ev) => onSexed(r.key, ev.target.checked)} aria-label="Sexado" />
                </td>
            </>
        );
    };

    return (
        <div className="cl-table-wrap">
            <table className="cl-table">
                <thead>
                    <tr>
                        {!sameSemen && <th className="cl-th-semen">Semen</th>}
                        <th>Óv.</th>
                        {STAGES.map((s) => <th key={s.value} className="cl-th-stage" style={{ color: s.color }} title={s.label}>{s.short}</th>)}
                        <th>Emb.</th>
                        <th>Preserv.</th>
                        <th>Sex.</th>
                        <th aria-label="Acciones" />
                    </tr>
                </thead>
                <tbody>
                    {groups.map((g) => {
                        const open = !collapsed.has(g.key);
                        const viables = g.donor?.viables ?? 0;
                        const totalEmb = g.items.reduce((a, r) => a + embryosCreated(r.stages), 0);
                        const over = totalEmb > viables;
                        const pct = viables > 0 ? Math.min(100, Math.round((totalEmb / viables) * 100)) : (totalEmb > 0 ? 100 : 0);
                        return (
                            <Fragment key={g.key}>
                                <tr className="cl-group">
                                    <td className="cl-group__cell" colSpan={colSpan}>
                                        <button type="button" className="cl-group__toggle" onClick={() => toggle(g.key)} aria-expanded={open} aria-label={open ? 'Colapsar' : 'Desplegar'}>
                                            <Chevron open={open} />
                                        </button>
                                        <span className="cl-group__name">{g.donor?.name || '—'}{g.donor?.number ? ` · ${g.donor.number}` : ''}</span>
                                        <span className={`cl-group__meter ${over ? 'cl-group__meter--over' : ''}`} title={`${totalEmb}/${viables} embriones`}>
                                            <span className="cl-donor-bar"><span className="cl-donor-bar__fill" style={{ width: `${pct}%` }} /></span>
                                            <span className="cl-donor-num">{totalEmb}/{viables} emb</span>
                                        </span>
                                        {!g.items.length && <span className="cl-group__count">sin clivage</span>}
                                        {!open && g.items.length > 1 && <span className="cl-group__count">{g.items.length} clivage(s)</span>}
                                    </td>
                                </tr>
                                {open && g.items.length === 0 && (
                                    <tr className="cl-tr cl-tr--clivage">
                                        <td className="cl-empty-cell" colSpan={colSpan - 1}>Esta donadora no tiene clivage en esta sesión.</td>
                                        <td className="cl-td-actions">
                                            <button type="button" className="cl-add-btn" onClick={() => onAddClivage(g.key)} aria-label="Agregar clivage" title="Agregar clivage a esta donadora"><IconPlus /></button>
                                        </td>
                                    </tr>
                                )}
                                {open && g.items.map((r, j) => {
                                    const isLast = j === g.items.length - 1;
                                    return (
                                        <tr key={r.key} className="cl-tr cl-tr--clivage">
                                            {fields(r)}
                                            <td className="cl-td-actions">
                                                <button type="button" className="cl-row__del" onClick={() => onRemove(r.key)} aria-label="Quitar" title={r.clivageId ? 'Eliminar clivage' : 'Quitar fila'}>
                                                    <IconTrash />
                                                </button>
                                                {isLast && (
                                                    <button type="button" className="cl-add-btn" onClick={() => onAddClivage(g.key)} aria-label="Agregar clivage a esta donadora" title="Otro toro (otro clivage para esta donadora)"><IconPlus /></button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
